import { Link } from "@tanstack/react-router";
import { ArrowLeft, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

import type { ManualDetail } from "@/api/db/manuals";
import { CONSULTATION_KIND_LABEL } from "@/api/manual/consultation";
import { UnsavedChangesGuard } from "@/components/shared/dialogs/unsaved-changes-guard";

import { DocumentStatus } from "./document-status";

const dirtyBanner = "Entrega indisponível com edições não salvas.";
const leaveConfirm = "As edições não salvas serão descartadas.";

interface DocumentChromeProps {
	manual: ManualDetail;
	dirty: boolean;
	toolbar: ReactNode;
	children: ReactNode;
}

export function DocumentChrome({ manual, dirty, toolbar, children }: DocumentChromeProps) {
	const blocking = dirty && manual.status !== "delivered";

	return (
		<header className="workspace-sticky">
			<div className="flex flex-col gap-3 px-4 py-2.5 md:flex-row md:flex-wrap md:items-center md:justify-between">
				<div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
					<Link
						to="/clinica"
						className="inline-flex items-center gap-1 rounded-sm text-sm text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						<ArrowLeft className="size-4" />
						Voltar
					</Link>

					<span className="min-w-0 truncate font-display text-base text-foreground">
						{manual.patient.name}
					</span>

					<span className="min-w-0 truncate text-xs text-muted-foreground">
						Consulta {CONSULTATION_KIND_LABEL[manual.patient.consultation.kind]}{" "}
						{manual.patient.consultation.date}
					</span>

					<DocumentStatus status={manual.status} />
				</div>

				<div className="flex flex-wrap items-center gap-2">{children}</div>
			</div>

			{toolbar}

			{blocking && (
				<p className="flex items-center gap-1.5 px-4 pb-2 text-sm font-semibold text-warning">
					<TriangleAlert className="size-4 shrink-0" aria-hidden />
					{dirtyBanner}
				</p>
			)}

			<UnsavedChangesGuard
				blocking={blocking}
				title="Edições não salvas"
				description={leaveConfirm}
				stayLabel="Ficar"
				leaveLabel="Sair mesmo assim"
			/>
		</header>
	);
}
